<template>
  <nav v-if="variant === 'breadcrumb'" class="journey-breadcrumb flex min-w-0 flex-wrap items-center gap-2 text-xs" :class="dark ? 'text-white/65' : 'text-slate-500'" aria-label="Breadcrumb">
    <a :href="getPath('/')" class="transition hover:text-amber-400" @click.prevent="goPath('/')">{{ content.home }}</a>
    <span :class="dark ? 'text-amber-300/50' : 'text-slate-300'">/</span>
    <span v-if="current === 'hub'" class="font-bold" :class="dark ? 'text-white' : 'text-slate-800'">{{ content.hubTitle }}</span>
    <a v-else :href="hubPath" class="font-semibold transition hover:text-amber-400" @click.prevent="goPath(localizedHubPath)">{{ content.hubTitle }}</a>
    <template v-if="currentPage">
      <span :class="dark ? 'text-amber-300/50' : 'text-slate-300'">/</span>
      <span class="font-bold" :class="dark ? 'text-white' : 'text-slate-800'">{{ currentPage.title }}</span>
    </template>
  </nav>

  <section v-else class="journey-links border-t border-slate-200 bg-[#eef3ef]">
    <div class="mx-auto max-w-screen-xl px-4 py-12 sm:px-6 lg:px-10 lg:py-16">
      <div class="mb-7 max-w-3xl">
        <span class="text-xs font-extrabold uppercase tracking-[.18em] text-teal-700">{{ content.sectionEyebrow }}</span>
        <h2 class="mt-2 text-2xl font-black tracking-tight text-slate-900 md:text-3xl">{{ content.sectionTitle }}</h2>
        <p class="mt-3 text-sm leading-7 text-slate-600">{{ content.sectionDescription }}</p>
      </div>
      <div class="grid gap-4 sm:grid-cols-2" :class="visiblePages.length > 3 ? 'lg:grid-cols-4' : 'lg:grid-cols-3'">
        <a v-for="page in visiblePages" :key="page.id" :href="pageHref(page)" :target="page.external ? '_blank' : undefined" :rel="page.external ? 'noopener' : undefined" class="journey-link-card group flex min-h-[205px] flex-col rounded-3xl border border-white bg-white p-5 shadow-[0_18px_45px_-36px_rgba(15,23,42,.65)] transition duration-300 hover:-translate-y-1 hover:border-teal-200 hover:shadow-xl" @click="openPage($event, page)">
          <span class="journey-link-icon flex h-11 w-11 items-center justify-center rounded-2xl" :class="toneClass(page.tone)"><font-awesome-icon :icon="page.icon" /></span>
          <h3 class="mt-5 text-lg font-black leading-snug text-slate-900 transition group-hover:text-teal-700">{{ page.title }}</h3>
          <p class="mt-2 text-sm leading-6 text-slate-500">{{ page.description }}</p>
          <span class="mt-auto flex items-center gap-2 pt-5 text-xs font-extrabold text-teal-700">{{ content.open }} <font-awesome-icon icon="fa-solid fa-arrow-right" class="h-3 w-3 transition group-hover:translate-x-1" /></span>
        </a>
      </div>
    </div>
  </section>
</template>

<script setup>
import { journeyNavigationContent } from '~/data/journey-pages'
import { useCommon } from '~/composables/useCommon'

const props = defineProps({
  current: { type: String, default: '' },
  variant: { type: String, default: 'footer' },
  dark: { type: Boolean, default: false }
})

const { locale } = useI18n()
const { getPath, goPath } = useCommon()
const language = computed(() => locale.value === 'en' ? 'en' : 'tr')
const content = computed(() => journeyNavigationContent[language.value])
const localizedHubPath = computed(() => language.value === 'en' ? '/page/journey-centre' : '/page/yolculuk-merkezi')
const hubPath = computed(() => getPath(localizedHubPath.value))
const currentPage = computed(() => content.value.pages.find(page => page.id === props.current))
const visiblePages = computed(() => content.value.pages.filter(page => page.id !== props.current))
const pageHref = page => page.external ? page.path : getPath(page.path)
const openPage = (event, page) => {
  if (page.external) return
  event.preventDefault()
  goPath(page.path)
}

const toneClass = tone => ({
  teal: 'bg-teal-50 text-teal-700',
  emerald: 'bg-emerald-50 text-emerald-700',
  amber: 'bg-amber-100 text-amber-700',
  sky: 'bg-sky-50 text-sky-700'
}[tone] || 'bg-slate-100 text-slate-700')
</script>

<style scoped>
.journey-links { margin: 0; padding: 0; }
.journey-links,
.journey-links p,
.journey-links h2,
.journey-links h3 { margin-bottom: 0; }
.journey-links h2 { line-height: 1.16; }
.journey-links h3 { line-height: 1.3; }
</style>
